import React, { useCallback, useContext, useRef, useState } from 'react';
import { message, Table, TablePaginationConfig, Button, Modal } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { compact } from 'lodash';
import { TourStoryType } from '../../../types/tourStory';
import { TOUR_STORIES } from '../../../constants/define/translation';
import { ActionsColumn, ShowHideColumn, TextColumn, TitleColumn } from './columns';
import { appStyles } from '../../../styles';
import { MESSAGE_ERROR } from '../../../constants/message';
import {
  postSortTourStoryRepository,
  postDeleteMultipleTourStoryRepository,
} from '../../../repository/mytm/tourStory/tourStoryRepository';
import { IndexColumnSort } from '../shared-column';
import { ConfirmAction } from '../../molecules/confirm/ConfirmAction';
import { CONFIG_CONTAINER_NAME, deleteBlob } from '../../../utils/azure';
import { postLoginRepository } from '../../../repository/mytm/auth/loginRepository';
import { UserContext } from '../../../repository/context/userContext';

interface TourStoryTableProps {
  records: TourStoryType[];
  onRefresh: () => void;
  setDataRecord: Function;
  handleChangeSwitchVideo: Function;
  handleChangeInputVideo: Function;
  removeItemByIndex: Function;
  handleAddVideo: Function;
  onOpenModal: Function;
  handleChangePageActive: Function;
  loading?: boolean;
  setLoading: (value: boolean) => void;
  className?: string;
  isDraft?: boolean;
  pagination?: TablePaginationConfig;
}

const DraggableBodyRow = ({ index, moveRow, className, style, ...restProps }: any) => {
  const ref = useRef();
  const type = 'DraggableBodyRow';

  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor: any) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: any) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));
  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ''}`}
      style={{ cursor: 'move', ...style }}
      {...restProps}
    />
  );
};

const { Column } = Table;

export const TourStoryTable = (props: TourStoryTableProps) => {
  const { t } = useTranslation(TOUR_STORIES);
  const userContext = useContext(UserContext);
  const {
    pagination,
    records,
    loading,
    setLoading,
    className,
    handleChangeSwitchVideo,
    handleChangeInputVideo,
    handleAddVideo,
    onOpenModal,
    onRefresh,
  } = props;

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const components: any = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      let dragRecordsList = [...records];
      const dragData = dragRecordsList[dragIndex];
      dragRecordsList.splice(dragIndex, 1);
      dragRecordsList.splice(hoverIndex, 0, dragData);
      dragRecordsList = dragRecordsList.filter(val => val.id);

      const sortList = dragRecordsList.map((record, index) => ({
        id: record.id,
        sortOrder: index + 1,
      }));

      postSortTourStoryRepository({
        sortOrderList: sortList,
      })
        .then(() => {
          setLoading(false);
          onRefresh();
        })
        .catch(() => {
          message.error(MESSAGE_ERROR.M02);
          setLoading(false);
        });
    },
    [records, onRefresh, setLoading],
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  const handleDelete = async () => {
    const tourStoryDelete = selectedRowKeys.map(key => records.find(record => record.index === key));
    const ids: any = compact(tourStoryDelete.map(record => record.id));
    if (!ids || ids.length === 0) {
      message.error(t('message.error.selectedDelete'));
      setSelectedRowKeys([]);
      return;
    }

    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.deleteMultiple')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setLoading(true);

        const updateFunc = () => {
          postDeleteMultipleTourStoryRepository({ ids })
            .then(() => {
              tourStoryDelete.forEach(record => {
                deleteBlob(record?.url, CONFIG_CONTAINER_NAME.UPLOADS).then().catch();
              });
              message.success(t('message.success.deleteMultipleTourStory'));
            })
            .catch(() => message.error(MESSAGE_ERROR.M02))
            .finally(() => {
              onRefresh();
              setSelectedRowKeys([]);
              setLoading(false);
            });
        };

        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setLoading(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const handleCancel = () => {
    setSelectedRowKeys([]);
  };

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        {selectedRowKeys.length > 0 && (
          <DeleteMultipleBlock>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SelectedInfo>{selectedRowKeys.length} rows selected</SelectedInfo>
              <CancelButton onClick={handleCancel}>Cancel</CancelButton>
            </div>
            <div>
              <DeleteButton onClick={handleDelete}>
                Delete <DeleteOutlined />
              </DeleteButton>
            </div>
          </DeleteMultipleBlock>
        )}
        <StyledTable
          dataSource={records}
          loading={loading}
          className={className}
          rowKey="index"
          pagination={pagination}
          components={components}
          rowSelection={rowSelection}
          summary={() => {
            return (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={2} index={0}>
                    Total: {pagination?.total}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            );
          }}
          onRow={(recordDeep: any, index?: number): any => {
            return {
              index,
              moveRow,
            };
          }}
        >
          <Column
            title={t('table.title.index')}
            align="center"
            width="5%"
            dataIndex="index"
            key="index"
            render={value => <IndexColumnSort value={value} />}
          />
          <Column
            title={t('table.title.type')}
            align="center"
            dataIndex="show"
            key="show"
            width="10%"
            render={(_, record: TourStoryType) => (
              <ShowHideColumn record={record} handleChangeSwitchVideo={handleChangeSwitchVideo} />
            )}
          />
          <Column
            title={t('table.title.source')}
            align="center"
            width="65%"
            key="title"
            render={(_, record: TourStoryType) => (
              <TitleColumn
                record={record}
                handleChangeInputVideo={handleChangeInputVideo}
                handleAddVideo={handleAddVideo}
                loading={loading}
                onOpenModal={onOpenModal}
              />
            )}
          />
          <Column
            title={t('table.title.views')}
            align="center"
            width="9%"
            key="views"
            render={(_, record: TourStoryType) => <TextColumn textStr={record?.totalView || 0} />}
          />
          <Column
            title={t('table.title.action')}
            align="center"
            key="action"
            width="8%"
            render={(_, record: TourStoryType) => <ActionsColumn total={records.length} record={record} {...props} />}
          />
        </StyledTable>
      </DndProvider>
    </>
  );
};

const StyledTable = styled(Table)`
  .ant-table-thead {
    border-bottom: 2px solid #eee;
    & > th > tr {
      ${appStyles.background.tableBgGray}
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid #dededee3;
      &:before {
        display: none;
      }
    }
  }

  .card-name-tiles {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 21px;
    max-height: 48px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  & th.action-home {
    text-align: center !important;
  }

  /* Customize scrollbar */
  .ant-table-body {
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    &::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }
`;

const DeleteMultipleBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 32px;
  height: 66px;
  background-color: #fff;
`;

const SelectedInfo = styled.span`
  font-size: 14px;
  font-weight: bold;
  color: #333;
`;

const CancelButton = styled(Button)`
  font-size: 14px;
  margin-right: 8px;
  color: #1890ff;
  font-weight: bold;
  border: none;
  background: none;
  &:hover {
    color: #40a9ff;
  }
`;

const DeleteButton = styled(Button)`
  width: 120px;
  height: 46px;
  background-color: #000;
  color: #fff;
  border-radius: 15px;
  padding-left: 27px;
  padding-top: -5px;
  display: flex;
  align-items: center;
  & .anticon-delete {
    margin-top: 3px;
    margin-left: -10px;
  }
  &:hover {
    background-color: #333;
  }
  .anticon {
    margin-left: 5px;
  }
`;
